import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Sparkles, ChevronRight, CheckCircle2, Heart } from 'lucide-react';

/**
 * DailyReflectionPrompt
 * — Phase 4 (option A): integrate, do not duplicate.
 * — On the Daily Alignment page we show four perception prompts only when
 *   today's MindFlō check-in has not been completed yet. Clicking through
 *   routes the user into the existing /mindflo flow which is the single
 *   source of truth for daily neuro-state. We never write our own
 *   `daily_reflection` documents.
 *
 * — When today's check-in already exists, we show a compact one-line
 *   confirmation with a "View MindFlō" link.
 */
const PROMPT_CARDS = [
  { key: 'energy',          label: 'Energy',          ask: 'How was your energy rhythm today?' },
  { key: 'gut',             label: 'Gut',             ask: 'How did your gut respond today?' },
  { key: 'nervous_system',  label: 'Nervous System',  ask: 'How was your nervous system today?' },
  { key: 'coherence',       label: 'Coherence',       ask: 'Did your system feel more coherent or more resistant?' },
];

export default function DailyReflectionPrompt({ subscriberId }) {
  const navigate = useNavigate();
  const [today, setToday] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/mindflo/today`)
      .then(r => { if (mounted) setToday(r.data || null); })
      .catch(() => {})
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [subscriberId]);

  if (loading) return null;

  const hasCheckin = !!today?.has_checkin_today;

  if (hasCheckin) {
    return (
      <div
        className="flex items-center gap-3 rounded-2xl border border-[#EBE8E3] bg-[#FBFaf9] px-4 py-3"
        data-testid="daily-reflection-already-done"
      >
        <CheckCircle2 className="h-4 w-4 text-[#4A5D4E] flex-shrink-0" strokeWidth={1.5} />
        <p className="text-sm text-[#3B3936] font-['Work_Sans'] flex-1">
          Today’s state has already been captured in MindFlō.
        </p>
        <button
          type="button"
          onClick={() => navigate('/mindflo')}
          className="text-xs text-[#8C6B55] hover:text-[#5C5955] font-['Work_Sans'] underline"
          data-testid="reflection-view-mindflo"
        >
          View MindFlō
        </button>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-[#EBE8E3] bg-white/70 p-5" data-testid="daily-reflection-prompt">
      <div className="flex items-center gap-2 mb-1">
        <Heart className="h-4 w-4 text-[#8C6B55]" strokeWidth={1.4} />
        <p className="text-[10px] uppercase tracking-[0.25em] text-[#8A8681] font-['Work_Sans']">
          Today’s State
        </p>
      </div>
      <h3 className="text-lg font-['Outfit'] text-[#2C2A29] mb-1">
        What did your body show today?
      </h3>
      <p className="text-xs text-[#5C5955] font-['Work_Sans'] mb-4 max-w-prose">
        Capture today’s state in MindFlō so your observations help CARE OS understand your system.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
        {PROMPT_CARDS.map(c => (
          <div
            key={c.key}
            className="rounded-xl bg-[#F4F2EE] px-3 py-2"
            data-testid={`reflection-prompt-${c.key}`}
          >
            <p className="text-[10px] uppercase tracking-[0.18em] text-[#8A8681] font-['Work_Sans']">
              {c.label}
            </p>
            <p className="text-xs text-[#3B3936] font-['Work_Sans'] mt-0.5 leading-snug">{c.ask}</p>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={() => navigate('/mindflo')}
        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-[#2C2A29] hover:bg-[#1C1A19] text-white font-['Work_Sans'] text-sm transition-colors"
        data-testid="reflection-save-state-btn"
      >
        <Sparkles className="h-3.5 w-3.5" strokeWidth={1.5} />
        Save Today’s State
        <ChevronRight className="h-3.5 w-3.5" />
      </button>
      <p className="text-[10px] text-[#8A8681] font-['Work_Sans'] mt-2">
        Your observation is saved in MindFlō, the single source of truth for daily state.
      </p>
    </div>
  );
}
